import { useNodes } from '@/service/useNodes';
import $http from './http';
import { decryptImgFn, imgSuffix } from '../MediaComp/helper';
import { Message } from '@arco-design/web-react';
import { $POST } from '@/service/request';
import { $PUT } from './multipartUploadApi';
import { localMemory } from 'localmemory';
import SparkMD5 from 'spark-md5';
import { simpleMediaSize, isSimpleMediaSize } from './tools';
import useUserStore from '@/store/userStore';
import axios from 'axios';
import { useLang } from '@/locales/useLang';

export interface ISrc {
  key: string;
  url: string;
}

export const calcChunkHash = async (chunk: Blob): Promise<string> => {
  return new Promise((resolve) => {
    // const spark = new SparkMD5();
    const reader = new FileReader();
    const spark = new SparkMD5.ArrayBuffer();

    reader.onload = function (e) {
      // 每个块的 ArrayBuffer
      const arrayBuffer = e?.target?.result as ArrayBuffer;

      // 更新 SparkMD5 实例
      arrayBuffer && spark.append(arrayBuffer);

      // 如果所有块都计算完了
      // 获取最终的 MD5 值
      const md5Hash = spark.end();
      console.log('计算出来的hash....', md5Hash);

      resolve(md5Hash);

      // 计算完毕后，可以清空 SparkMD5 实例，准备计算下一个文件的哈希
      spark.reset();
    };

    // 读取当前块的 ArrayBuffer
    reader.readAsArrayBuffer(chunk);
  });
};

let lockClick = false;

export const useUpload = () => {
  const { nodes, getNodes } = useNodes();
  const userStore = useUserStore();
  const $t = useLang();

  const uploadQueueKey = `uploadQueue-${userStore.sessionConfig.sessionId}-${userStore.sessionConfig.nickId}`;

  const progressNum = useRef(0);

  const base64Src = useRef('');

  const uploadInfo = useRef<any>(null);

  const activeSrc = useRef<ISrc>({
    url: '',
    key: '',
  });

  const uploading = useRef(false);
  const uploaded = useRef(false);

  const doUpload = async (
    file: File,
    isEncrypt: boolean = true,
    onProgress?: (progress: number) => void,
    onStop?: (uploadId: string) => boolean,
    type?: 'repeat',
    uploadId?: string,
    initDone?: () => void
  ): Promise<ISrc> => {
    if (isSimpleMediaSize(file.size)) {
      // handle simple media
      return await doSimpleUpload(file, onProgress, isEncrypt);
    } else {
      if (type === 'repeat') {
        return continuePartUpload(file, onProgress!, onStop!, uploadId!);
      }

      return doMultiplePartUpload(file, onProgress, onStop, initDone);
    }
  };

  /**
   * handle simple upload
   */
  const doSimpleUpload = async (
    file: File,
    onProgress?: (progress: number) => void,
    isEncrypt: boolean = true
  ): Promise<ISrc> => {
    // const uploadList = ['http://oss-dev.dreaminglife.cn/api/single/fileupload'];
    console.log('简单', file);
    const nodes = await getNodes?.();
    const uploadList = nodes.oss?.map((item) => {
      return (item += '/api/single/fileupload');
    });

    // const uploadList = nodes.oss;

    uploading.current = true;
    let inx = 0;

    const core = async () => {
      const url = uploadList[inx];

      const fd = new FormData();
      fd.append('file', file);
      if (isEncrypt) {
        console.log('file type...', file.type);
        if (imgSuffix.includes('.' + file.type.split('/')?.[1])) {
          fd.append('sign_type', '2');
        } else {
          fd.append('sign_type', '1');
        }
      }

      try {
        const resp: any = await $http.post(url, fd, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress(progressEvent) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent?.total!
            );
            console.log(`Upload Progress: ${percentCompleted}%`);
            progressNum.current = percentCompleted;
            onProgress?.(percentCompleted);
          },
        });

        const keyValue = resp?.paths || [];
        uploadInfo.current = keyValue;

        uploaded.current = true;
        uploading.current = false;
      } catch (error) {
        uploadInfo.current = { key: '', url: '' };
      }
    };

    try {
      await core();
    } catch (error) {
      inx++;

      if (inx >= uploadList.length) {
        Message.error($t('chat_session_status_31'));
        throw new Error();
      }

      await core();
    }

    uploaded.current = true;
    uploading.current = false;

    return uploadInfo.current?.[0];
  };

  /**
   * handle multiple part upload
   */
  const doMultiplePartUpload = async (
    f: File,
    onProgress?: (progress: number) => void,
    onStop?: (uploadId: string) => boolean,
    initDone?: () => void
  ): Promise<ISrc> => {
    // const { files } = e.target;
    // const uploadList = ['http://oss-dev.dreaminglife.cn/api/single/fileupload'];

    const uploadList: any[] = [];

    const nodes = await getNodes?.();

    const initiateUrl = nodes.oss?.[0] + '/api/multi/initiate-multipart_upload';

    const resp = $POST(initiateUrl, {
      size: f.size,
      name: f.name,
      contentType: 'application/json',
    });

    uploadList.push(
      new Promise((resolve, reject) => {
        resp
          .then((res) => {
            resolve({
              file: f,
              uploadInfo: res,
            });
          })
          .catch(() => {
            reject({
              file: f,
              uploadInfo: '',
            });
          });
      })
    );

    const uploadInfoList = await Promise.all(uploadList).catch((error) => []);

    const uploadId = uploadInfoList?.[0]?.uploadInfo?.data?.uploadId;

    const uploadQueue = localMemory.getItem(uploadQueueKey);
    const uploadTarget = uploadQueue?.[uploadId]; // 检索之前上传的某个文件
    const hadUploadedInx = uploadTarget?.length ?? 0; // 检索之前上传的某个文件的 index
    // const hadUploadedInx = 0; // 检索之前上传的某个文件的 index

    const newUploadItem: {
      totalNodes: any[];
      uploadedInx: number[];
      list: { nodeItem: any; etag: string }[];
    } = {
      totalNodes: uploadInfoList?.[0].uploadInfo.data.node,
      uploadedInx: [],
      list: [],
    };
    initDone?.();
    const t = onStop?.(uploadId);
    if (t) {
      const uploadQueue = localMemory.getItem(uploadQueueKey);
      const newQueue = uploadQueue
        ? { ...uploadQueue, [uploadId]: newUploadItem }
        : { [uploadId]: newUploadItem };

      localMemory.setItem({
        name: uploadQueueKey,
        value: newQueue,
      });

      return { key: '', url: '' };
    }

    for (const upItem of uploadInfoList) {
      let curChunkIndex = 0;

      const uploadCount: any[] = [];
      const uploadedInxCount: number[] = [];

      const getUploadCount = async () => {
        const newUploadItem: {
          totalNodes: any[];
          uploadedInx: number[];
          list: { nodeItem: any; etag: string }[];
        } = {
          totalNodes: upItem.uploadInfo.data.node,
          uploadedInx: [],
          list: [],
        };

        const t = onStop?.(uploadId);
        if (t) {
          const uploadQueue = localMemory.getItem(uploadQueueKey);
          const newQueue = uploadQueue
            ? { ...uploadQueue, [uploadId]: newUploadItem }
            : { [uploadId]: newUploadItem };

          localMemory.setItem({
            name: uploadQueueKey,
            value: newQueue,
          });

          return;
        }

        for (let i = 0, len = upItem.uploadInfo.data.node.length; i < len; i++) {
          const t = onStop?.(uploadId);
          if (t) {
            console.log('stop222...', uploadId, newUploadItem);

            const uploadQueue = localMemory.getItem(uploadQueueKey);
            const newQueue = uploadQueue
              ? { ...uploadQueue, [uploadId]: newUploadItem }
              : { [uploadId]: newUploadItem };

            console.log('newUploadItem....', uploadId, newUploadItem);
            localMemory.setItem({ name: uploadQueueKey, value: newQueue });

            const uploadQueue2 = localMemory.getItem(uploadQueueKey);
            console.log('uploadQueue2....', uploadQueue2);
            break;
          }

          const nodeItem = upItem.uploadInfo.data.node[i];
          console.log('curChunkIndex111...', curChunkIndex);

          let chunk;
          try {
            console.log('upItem.file...', upItem.file);
            chunk = upItem.file.slice(curChunkIndex, curChunkIndex + nodeItem.size);
          } catch (error) {
            console.log('chunk...报错', error);
          }

          curChunkIndex += nodeItem.size; // 叠块

          // 计算etag: chunk进行md5 hash
          const etag = await calcChunkHash(chunk);
          console.log('chunk....', etag);

          if (onStop?.(uploadId)) {
            console.log('stop333...', uploadId, newUploadItem);

            const uploadQueue = localMemory.getItem(uploadQueueKey);
            const newQueue = uploadQueue
              ? { ...uploadQueue, [uploadId]: newUploadItem }
              : { [uploadId]: newUploadItem };

            localMemory.setItem({ name: uploadQueueKey, value: newQueue });

            const uploadQueue2 = localMemory.getItem(uploadQueueKey);
            console.log('uploadQueue2....', uploadId, uploadQueue2);
            break;
          }

          const source = axios.CancelToken.source();

          const promiseResp: any = $PUT(nodeItem.url, chunk, {
            headers: {
              ...nodeItem.header,
              'Content-Type': 'application/octet-stream',
            },
            cancelToken: source.token,
          });

          if (onStop?.(uploadId)) {
            console.log('stop444...', uploadId, newUploadItem);

            const uploadQueue = localMemory.getItem(uploadQueueKey);
            const newQueue = uploadQueue
              ? { ...uploadQueue, [uploadId]: newUploadItem }
              : { [uploadId]: newUploadItem };

            localMemory.setItem({ name: uploadQueueKey, value: newQueue });

            const uploadQueue2 = localMemory.getItem(uploadQueueKey);
            console.log('uploadQueue2....', uploadId, uploadQueue2);

            source.cancel();
            console.log('cancel...', nodeItem.url);
            break;
          }

          const resp = await promiseResp;

          if (onStop?.(uploadId)) {
            console.log('stop555...', uploadId, newUploadItem);

            const uploadQueue = localMemory.getItem(uploadQueueKey);
            const newQueue = uploadQueue
              ? { ...uploadQueue, [uploadId]: newUploadItem }
              : { [uploadId]: newUploadItem };

            localMemory.setItem({ name: uploadQueueKey, value: newQueue });

            const uploadQueue2 = localMemory.getItem(uploadQueueKey);
            console.log('uploadQueue2....', uploadId, uploadQueue2);

            source.cancel();
            console.log('cancel222...', nodeItem.url);
            break;
          }
          console.log('7777...', uploadId, newUploadItem);

          uploadedInxCount.push(i);
          newUploadItem.uploadedInx = uploadedInxCount;
          newUploadItem.list.push({ nodeItem, etag });

          // const newUploadItem = {
          //   [upItem.uploadInfo?.data?.uploadId]: { url: nodeItem.url, etag },
          // };

          // if (uploadQueue) {
          // 如果之前有存，则找到对应的uploadId,把 newUploadItem 塞进去
          // }

          const percentCompleted = Math.round((i / len) * 100);
          console.log('进度:::', percentCompleted);

          try {
            onProgress?.(percentCompleted);
          } catch (error) {
            console.log('onProgress error', error);
          }

          uploadCount.push({
            ...nodeItem,
            chunk,
            etag: etag,
          });

          if (onStop?.(uploadId)) {
            console.log('stop444...', uploadId, newUploadItem);

            const uploadQueue = localMemory.getItem(uploadQueueKey);
            const newQueue = uploadQueue
              ? { ...uploadQueue, [uploadId]: newUploadItem }
              : { [uploadId]: newUploadItem };

            localMemory.setItem({ name: uploadQueueKey, value: newQueue });

            const uploadQueue2 = localMemory.getItem(uploadQueueKey);
            console.log('uploadQueue2....', uploadQueue2);
            break;
          }
        }
      };

      await getUploadCount();

      if (onStop?.(uploadId)) {
        const uploadQueue = localMemory.getItem(uploadQueueKey);

        console.log('stop555', uploadId, uploadQueue);

        break;
      }
      const resp = uploadCount;
      console.log('获取 upload count...', resp);

      // const resp = await Promise.all(uploadCount);

      const tempCount = [];
      for (let i = 0, len = resp.length; i < len; i++) {
        const respItem = resp[i];
        tempCount.push({
          partNumber: i + 1,
          etag: respItem.etag,
        });
      }

      if (onStop?.(uploadId)) {
        console.log('stop666');

        break;
      }

      const completeUrl = nodes.oss?.[1] + '/api/multi/complete-multipart-upload';

      const result = await $POST<any>(completeUrl, {
        uploadId: upItem.uploadInfo.data.uploadId,
        name: upItem.file.name,
        part: tempCount,
      });

      return { key: '', url: result.data.location };
    }

    return { key: '', url: '' };
  };

  /**
   * multiple upload contine
   */
  const continuePartUpload = async (
    f: File,
    onProgress: (progress: number) => void,
    onStop: (uploadId: string) => boolean,
    uploadId: string
  ) => {
    if (lockClick) {
      return { key: '', url: '' };
    }
    lockClick = true;

    const uploadQueue = localMemory.getItem(uploadQueueKey);
    const uploadTarget = uploadQueue?.[uploadId]; // 检索之前上传的某个文件

    // Math.floor(uploadTarget.uploadedInx.length / uploadTarget.totalNodes.length)
    let latestInx;
    let resp;

    latestInx = uploadTarget?.uploadedInx?.[uploadTarget?.uploadedInx?.length - 1] || 0;
    const latestSize = uploadTarget?.totalNodes?.[latestInx]?.size || 0;

    let curChunkIndex = (latestInx + 1) * latestInx ? latestSize : 0;
    const uploadCount: any[] = [];
    const uploadedInxCount: number[] = uploadTarget?.uploadedInx || [];

    const newUploadItem: {
      totalNodes: any[];
      uploadedInx: number[];
      list: { nodeItem: any; etag: string }[];
    } = {
      ...uploadTarget,
    };

    const getUploadCount = async () => {
      console.log('for...2...', uploadId, uploadTarget);

      for (let i = 0, len = uploadTarget.totalNodes.length; i < len; i++) {
        if (onStop?.(uploadId)) {
          return false;
        }
        // const uploadQueue = localMemory.getItem(uploadQueueKey);
        // const uploadTarget = uploadQueue?.[uploadId]; // 检索之前上传的某个文件

        // console.log('for...3', uploadTarget.uploadedInx);
        if (uploadTarget.uploadedInx.includes(i)) {
          continue;
        }

        console.log('for...', i);

        const nodeItem = uploadTarget.totalNodes[i];

        console.log('nodeItem...', nodeItem, f);

        console.log('curChunkIndex...', curChunkIndex);
        let chunk;
        try {
          chunk = f.slice(curChunkIndex, curChunkIndex + nodeItem.size);
        } catch (error) {
          console.log('chunk...报错', error);
        }

        curChunkIndex += nodeItem.size; // 叠块

        // 计算etag: chunk进行md5 hash
        const etag = await calcChunkHash(chunk!);

        const source = axios.CancelToken.source();

        const promiseResp: any = $PUT(nodeItem.url, chunk, {
          headers: {
            ...nodeItem.header,
            'Content-Type': 'application/octet-stream',
          },
          cancelToken: source.token,
        });

        if (onStop?.(uploadId)) {
          console.log('stop444...', uploadId, newUploadItem);

          const uploadQueue = localMemory.getItem(uploadQueueKey);
          const newQueue = uploadQueue
            ? { ...uploadQueue, [uploadId]: newUploadItem }
            : { [uploadId]: newUploadItem };

          localMemory.setItem({ name: uploadQueueKey, value: newQueue });

          const uploadQueue2 = localMemory.getItem(uploadQueueKey);
          console.log('uploadQueue2....', uploadId, uploadQueue2);

          source.cancel();
          console.log('cancel...', nodeItem.url);
          return false;
        }

        const resp = await promiseResp;

        if (onStop?.(uploadId)) {
          console.log('stop555s...', uploadId, newUploadItem);

          const uploadQueue = localMemory.getItem(uploadQueueKey);
          const newQueue = uploadQueue
            ? { ...uploadQueue, [uploadId]: newUploadItem }
            : { [uploadId]: newUploadItem };

          localMemory.setItem({ name: uploadQueueKey, value: newQueue });

          const uploadQueue2 = localMemory.getItem(uploadQueueKey);

          source.cancel();
          return false;
        }

        uploadTarget.list.push({ nodeItem, etag });
        uploadTarget.uploadedInx.push(i);
        const newQueue = { ...uploadQueue, ...uploadTarget };
        console.log('for...4...', newQueue);
        localMemory.setItem({ name: uploadQueueKey, value: newQueue });

        // uploadedInxCount.push(i);
        // newUploadItem.uploadedInx = uploadedInxCount;
        // newUploadItem.list.push({ nodeItem, etag });

        // const newUploadItem = {
        //   [upItem.uploadInfo?.data?.uploadId]: { url: nodeItem.url, etag },
        // };

        // if (uploadQueue) {
        // 如果之前有存，则找到对应的uploadId,把 newUploadItem 塞进去
        // }

        const percentCompleted = Math.round((i / len) * 100);
        console.log('进度:::', percentCompleted);

        try {
          onProgress?.(percentCompleted);
        } catch (error) {
          console.log('onProgress error', error);
        }

        // uploadCount.push({
        //   ...nodeItem,
        //   chunk,
        //   etag: etag,
        // });

        if (onStop?.(uploadId)) {
          console.log('stop888');

          // const newQueue = uploadQueue
          //   ? { ...uploadQueue, [uploadId]: newUploadItem }
          //   : { [uploadId]: newUploadItem };

          return false;
        }
      }
    };
    try {
      resp = await getUploadCount().catch((err) => {});
      lockClick = false;
    } catch (error) {}
    if (resp === false) {
      return { key: '', url: '' };
    }

    const beforePart = uploadTarget.list.map((item: any, i: number) => {
      return {
        partNumber: i + 1,
        etag: item.etag,
      };
    });

    const nodes = await getNodes?.();

    const completeUrl = nodes.oss?.[1] + '/api/multi/complete-multipart-upload';

    const result = await $POST<any>(completeUrl, {
      uploadId: uploadId,
      name: f.name,
      part: [...beforePart],
    });

    return { key: '', url: result.data.location };
  };

  const base64DecodeSuccess = async () => {
    for (let i = 0, len = uploadInfo.current.length; i < len; i++) {
      const item = uploadInfo.current[i];
      try {
        // 解码成功，后面就不尝试了
        const resp = await decryptImgFn(item);
        // console.log('解码后...', resp);
        base64Src.current = resp;
        activeSrc.current.url = item.url;
        activeSrc.current.key = item.key;
        break;
      } catch (error) {
        console.log('decode error...');
      }
    }
  };

  return {
    progressNum,
    doUpload,
    continuePartUpload,
  };
};
